import { supertypeClass, Bindable, Remoteable, Persistable, Supertype } from '@haventech/amorphic';
import { CdmService } from '../../../../../common/js/services/CdmService';
import { RateClasses, NonTobaccoPlus } from '../../../../../common/js/static/RateClasses';
import { CoverpathWholeLifeQuote } from '../../../quote/coverpath/CoverpathWholeLifeQuote';
import { HtmlPdfUtils } from '../../HtmlPdfUtils';
import { IllustrationRequest } from '../../IllustrationRequest';
import { constants } from '../../basicIllustration/templates/constantsForPdf';
import { CPPolicyRateClassDescriptions } from '../../../../js/interfaces/CoverpathTypes';
import { Assets } from '../../basicIllustration/templates/common/Assets';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { StateSpecification } from '../../basicIllustration/templates/common/StateSpecification';
import { Product } from '../../../../../common/js/static/Product';
import { mmopmProductBlocks, ProductType } from '../../HtmlPdfTypes';
import { CommonUtils } from '../../../../../common/js/static/CommonUtils';

export class CssStyle {
  HtmlPdfCoverPageStyle: string = '';
}

class CoverPage {
  BankSaleDisclosure: string = '';
  HtmlPdfCoverPage: string = '';
  LowerDividendProductLabel: string = '';
  css: CssStyle = new CssStyle();
}

export type Body = {
  html: string;
  formOrder?: string;
  formType?: string;
  options?: any;
  footer?: string;
};

@supertypeClass({ toClient: false, toServer: false })
export class HtmlPdfCoverPage extends Bindable(Remoteable(Persistable(Supertype))) {
  //protected basicIllustrationData;
  private agentStateLicenseService: CdmService;
  private readonly cpToMMDividends: Map<string, string> = new Map([
    ['Paid-up Additions', 'Paid-Up Additions'],
    ['Cash', 'Paid in Cash'],
    ['Reduce Premium/Paid-up Additions', 'Reduce Premium, excess to Paid-Up Additions'],
    ['Reduce Premium/Cash', 'Reduce Premium, excess to Cash'],
    ['Dividend Accumulations', 'Accumulate at Interest'],
  ]);
  private templateFolder = './apps/coverpath-common/classes/wholelife/components/coverpage/templates/';
  constructor() {
    super();
    this.agentStateLicenseService = new CdmService();
  }

  CoverPage;

  // used by inforce illustrations only
  async getHtmlContent_coverPage(
    illustrationRequest: IllustrationRequest,
    inforceProductType: ProductType,
    productBlock?
  ): Promise<string> {
    if (this.CoverPage === undefined) {
      this.CoverPage = await HtmlPdfUtils.getContent(this.templateFolder, new CoverPage());
    }

    const productCode = illustrationRequest.productCode;
    const isCAS = HtmlPdfUtils.isCASProduct(productCode);
    const term = illustrationRequest.term;
    const style = `${this.CoverPage.css.HtmlPdfCoverPageStyle}`;
    const theProducts = Product.products;
    const productName = this.getProductName(productBlock, term, productCode);
    let body: string = this.CoverPage.HtmlPdfCoverPage;
    if (inforceProductType === ProductType.Term) {
      body = body
        .replace(/{{SUB_HEADER}}/, 'Inforce Basic Life Insurance Policy Presentation')
        .replace(/{{PRODUCT}}/, productName);
    } else if (inforceProductType === ProductType.WholeLife) {
      body = body
        .replace(/{{SUB_HEADER}}/, 'Inforce Basic Life Insurance Policy Presentation')
        .replace(/{{PRODUCT}}/, `Whole Life Legacy ${term}`);
    }

    let coverPageHtml = HtmlPdfUtils.getHtmlContent(style, body);
    coverPageHtml = await this.replaceHtmlContentAgentInfo(illustrationRequest, coverPageHtml);
    coverPageHtml = this.replaceBankSaleDisclosure(illustrationRequest, coverPageHtml);
    return this.replaceHtmlContent(illustrationRequest, coverPageHtml);
  }

  getProductName(productBlock, term, productCode): string {
    switch (productBlock) {
      case mmopmProductBlocks.MMOPMTerm[2005]:
      case mmopmProductBlocks.MMOPMTerm[2004]:
        return 'Term 20 Guaranteed';
      case mmopmProductBlocks.MMOPMTerm[99]:
        return 'Term 20 Guaranteed-99';
      case mmopmProductBlocks.MMOPMTermAPT80[2006]:
        return 'ART';
      case mmopmProductBlocks.MMOPMTermAPT80[99]:
        return 'Adjustable Premium Term 80';
      default: {
        let fullName = Product.products[productCode].name;
        fullName += ` - ${term === 1 ? 'ART' : term}`;
        if (productCode === Product.products.MMTRADTerm.code) {
          fullName += CommonUtils.isEcpProduct(productCode) ? ' ECP' : '';
        } else if (productCode === Product.products.MMTRADTermECP.code) {
          fullName += `<span class="titlenewline"></span>with ECP - an Extended Conversion Period`;
        }
        return fullName;
      }
    }
  }

  // replace the HTML content for coverpage with illustration data
  replaceHtmlContent(illustrationRequest: IllustrationRequest, htmlContent) {
    //check if either a)mecStatus has been set already in illustrationRequest determined by Exchange1035 field on quote page
    //or b)mecYear returned from product illustration call is > 0
    let underwritingClass;
    let rateClassDescription;

    if (illustrationRequest.rateClass === RateClasses.NON_TOBACCO_PLUS.stringCode) {
      underwritingClass = NonTobaccoPlus.rateClassDescription.tobaccoDescription;
      rateClassDescription = NonTobaccoPlus.rateClassDescription;
    } else {
      underwritingClass = CPPolicyRateClassDescriptions[illustrationRequest.rateClass].tobaccoDescription;
      rateClassDescription = CPPolicyRateClassDescriptions[illustrationRequest.rateClass];
    }

    const rateClass = RateClasses.findByStringCode(illustrationRequest.rateClass);
    let underWritingClassForFooter = RateClasses.isTableRated(rateClass.code)
      ? rateClassDescription.longDescription
      : rateClassDescription.tobaccoDescription;

    if (HtmlPdfUtils.isInsuredJuvenile(illustrationRequest)) {
      underwritingClass = 'Juvenile';
      underWritingClassForFooter = 'Juvenile';
    }

    return (
      htmlContent
        .replace(
          /{{LowerDividendWarning}}/,
          illustrationRequest.dividendInterestAdjustment > 0 ? this.CoverPage.LowerDividendProductionLabel : ''
        )
        .replace(/{{ClientFirstName}}/g, illustrationRequest.customerPerson.firstName)
        .replace(/{{ClientLastName}}/g, illustrationRequest.customerPerson.lastName)
        .replace(/{{PolicyNumber}}/g, illustrationRequest.policyNumber)
        .replace(/{{URL_FOR_LOGO}}/g, Assets.MassMutual_Logo)
        .replace(/{{CurrentDate}}/g, HtmlPdfUtils.getDateInPrettyFormat())
        .replace(/{{CopyrightYear}}/g, CoverpathUtils.getCopyrightYear())
        .replace(/{{TemporaryFlatExtraYears}}/g, constants.TemporaryFlatExtraYears)
        .replace(
          /{{PolicyFormNumber}}/g,
          HtmlPdfUtils.getPolicyFormNumber(illustrationRequest.state, illustrationRequest.productCode)
        )
        .replace(
          /{{ClientGender}}/g,
          HtmlPdfUtils.getGenderInPrettyFormat(illustrationRequest.gender, illustrationRequest.state)
        )
        .replace(/{{ClientAge}}/g, illustrationRequest.issueAge)
        .replace(/{{CRNValue}}/g, this.amorphic.config.nconf.get('wholeLifeBasicIllustrationCRN'))
        //we use the insuranceQuote datamodel for the descriptions, but look up the value as specified in the request
        .replace(/{{UnderwritingClass}}/g, underwritingClass)
        .replace(
          /{{DividendOption}}/g,
          this.cpToMMDividends.get(
            new CoverpathWholeLifeQuote().__descriptions__('dividend')[illustrationRequest.dividendOption]
          )
        )
        .replace(/{{PremiumPaymentMode}}/g, illustrationRequest.paymentFrequency)
        .replace(/{{UnderwritingClassForFooter}}/g, underWritingClassForFooter)
    );
  }

  async replaceHtmlContentAgentInfo(illustrationRequest: IllustrationRequest, htmlContent: string) {
    const agentLicenseNumber = await this.getStateLicenseContent(illustrationRequest);

    return htmlContent
      .replace(/{{AgentFirstName}}/g, illustrationRequest.agent.firstName)
      .replace(/{{AgentLastName}}/g, illustrationRequest.agent.lastName)
      .replace(/{{AgentAddressLine1}}/g, illustrationRequest.agent.address.street)
      .replace(/{{AgentCity}}/g, illustrationRequest.agent.address.city)
      .replace(/{{AgentState}}/g, illustrationRequest.agent.address.state)
      .replace(/{{AgentZipCode}}/g, illustrationRequest.agent.address.zip)
      .replace(/{{AgentLicenseNumber}}/g, agentLicenseNumber)
      .replace(/{{AgentPhoneNumber}}/g, HtmlPdfUtils.getAgentPhone(illustrationRequest));
  }

  async getStateLicenseContent(illustrationRequest: IllustrationRequest): Promise<string> {
    if (
      StateSpecification.LicenseRequiredStates.includes(illustrationRequest.state) &&
      !illustrationRequest.isWholesaler
    ) {
      let { licenseNumber } = illustrationRequest;
      if (!licenseNumber) {
        licenseNumber = await this.fetchStateLicense(illustrationRequest.agent.producerId, illustrationRequest.state);
      }
      return `<p class="p3 ft3">Agent License Number: ${licenseNumber} </p>`;
    }
    return '';
  }

  private async fetchStateLicense(producerId: string, state: string): Promise<string> {
    const licenses = await this.agentStateLicenseService.getStateLicenses(producerId);
    const stateLicense = licenses.find((license) => license.state === state);
    if (stateLicense) {
      return stateLicense.licenseNumber;
    }
  }

  replaceBankSaleDisclosure(illustrationRequest: IllustrationRequest, htmlContent: string) {
    return htmlContent.replace(
      /{{BankSaleDisclosure}}/,
      illustrationRequest.bankSale ? this.CoverPage.BankSaleDisclosure : ''
    );
  }
}
